import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { IoDocumentTextOutline } from "react-icons/io5";
import { FiSend } from "react-icons/fi";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import { toast } from "react-toastify";
import { projectStatusList } from "../../jsons/projectStatusList";
import { City, State } from "country-state-city";
import LoadingComponent from "../../Components/LoadingComponent";

export default function RegisterProject() {
  const { token } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [allStates, setAllStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    const states = State.getStatesOfCountry("IN");
    setAllStates(states);
  }, []);

  useEffect(() => {
    const selectedState = watch("state");
    if (selectedState) {
      const citiesList = City.getCitiesOfState("IN", selectedState);
      setCities(citiesList);
    } else {
      setCities([]); // clear if no state selected
    }
  }, [watch("state")]);

  const handleRegisterProject = async (data) => {
    try {
      setLoading(true);
      const { data: resData } = await axios.post(`${server}/domain/new`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          projectType: data?.projectType,
          projectName: data?.projectName,
          description: data?.description,
          startDate: data?.startDate ? Date.parse(data?.startDate) : "",
          endDate: data?.endDate ? Date.parse(data?.endDate) : "",
          status: data?.status,
          projectOwner: data?.projectOwner,
          state: data?.state,
          district: data?.district,
        },
      });
      toast.success(resData?.message);
      setLoading(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center p-4">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="bg-white p-8 rounded-3xl shadow-xl w-full max-w-lg border"
      >
        {/* Title */}
        <div className="flex items-center gap-3 mb-6">
          <IoDocumentTextOutline className="text-3xl text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-800">
            Register New Project
          </h2>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(handleRegisterProject)}
          className="space-y-5"
        >
          {/* Project Type */}
          <div className="mt-6">
            <label className="block mb-1 font-medium text-gray-700">
              Project Type
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="Network"
                  {...register("projectType", {
                    required: "Project type is required",
                  })}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-gray-700 font-medium">Network</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="Software"
                  {...register("projectType", {
                    required: "Project type is required",
                  })}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-gray-700 font-medium">Software</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="Turnkey"
                  {...register("projectType", {
                    required: "Project type is required",
                  })}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-gray-700 font-medium">Turnkey</span>
              </label>
            </div>
            {errors.projectType && (
              <p className="text-sm text-red-500 mt-1">
                {errors.projectType.message}
              </p>
            )}
          </div>

          {/* Project Name */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Project Name
            </label>
            <input
              type="text"
              {...register("projectName", {
                required: "Project name is required",
              })}
              placeholder="Enter your project name"
              className={`h-11 px-4 w-full border ${
                errors.projectName ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.projectName && (
              <p className="text-sm text-red-500 mt-1">
                {errors.projectName.message}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Description
            </label>
            <textarea
              {...register("description", {
                required: "Description is required",
                minLength: {
                  value: 10,
                  message: "Description must be at least 10 characters",
                },
              })}
              placeholder="Briefly describe the project"
              rows="4"
              className={`px-4 py-2 w-full border ${
                errors.description ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 resize-none`}
            />
            {errors.description && (
              <p className="text-sm text-red-500 mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Start Date */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Start Date
            </label>
            <input
              type="date"
              {...register("startDate", { required: false })}
              className={`h-11 px-4 w-full border ${
                errors.startDate ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.startDate && (
              <p className="text-sm text-red-500 mt-1">
                {errors.startDate.message}
              </p>
            )}
          </div>

          {/* End Date */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              End Date
            </label>
            <input
              type="date"
              {...register("endDate", { required: false })}
              className={`h-11 px-4 w-full border ${
                errors.endDate ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.endDate && (
              <p className="text-sm text-red-500 mt-1">
                {errors.endDate.message}
              </p>
            )}
          </div>

          {/* Project Owner */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Project Owner
            </label>
            <input
              type="text"
              {...register("projectOwner", {
                required: "Project owner is required",
              })}
              placeholder="Enter project owner name"
              className={`h-11 px-4 w-full border ${
                errors.projectOwner ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.projectOwner && (
              <p className="text-sm text-red-500 mt-1">
                {errors.projectOwner.message}
              </p>
            )}
          </div>

          {/* State */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              State
            </label>
            <input
              type="text"
              {...register("state", {
                required: "State is required",
              })}
              placeholder="Enter state name"
              className={`h-11 px-4 w-full border ${
                errors.state ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.state && (
              <p className="text-sm text-red-500 mt-1">
                {errors.state.message}
              </p>
            )}
          </div>

          {/* District */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              District
            </label>
            <input
              type="text"
              {...register("district", {
                required: "District name is required",
              })}
              placeholder="Enter district name"
              className={`h-11 px-4 w-full border ${
                errors.district ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.district && (
              <p className="text-sm text-red-500 mt-1">
                {errors.district.message}
              </p>
            )}
          </div>

          {/* Status */}
          <div className="mt-6">
            <label className="block mb-1 font-medium text-gray-700">
              Status
            </label>
            <select
              {...register("status", { required: "Status is required" })}
              className={`h-11 px-4 w-full border ${
                errors.status ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            >
              <option value="">Select an option</option>
              {projectStatusList?.map((e, i) => (
                <option key={i} value={e?.slug}>
                  {e?.label}
                </option>
              ))}
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
            {errors.status && (
              <p className="text-sm text-red-500 mt-1">
                {errors.status.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            type="submit"
            className="w-full flex items-center justify-center gap-2 h-11 bg-blue-600 text-white rounded-lg font-semibold tracking-wide hover:bg-blue-700 transition duration-300"
          >
            <FiSend className="text-lg" />
            Register Project
          </motion.button>
        </form>
      </motion.div>

      {loading && <LoadingComponent />}
    </div>
  );
}
